import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";

import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";

import TopHeader from "./TopHeader";
import HeaderAccount from "./HeaderAccount";
import MiniHeader from "./MiniHeader";
import PendingCard from "./PendingCard/PendingCard";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";

const backend = await new Backend().init();

export default function Pending() {
  const windowSize = useSize();

  const [loading, setLoading] = useState(false);

  const [pending, setPending] = useState([]);

  const [apending, setAPending] = useState(0);
  const [executed, setExecuted] = useState(0);
  const [rejected, setRejected] = useState(0);

  const [sortBy, setSortBy] = useState("time_desc");

  const [start] = useState(moment().startOf("week"));
  const [end] = useState(moment().endOf("week"));

  const getUserData = useCallback(
    () => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      start,
      end,
    }),
    [start, end]
  );

  const loadPending = useCallback(
    async (startDate = start, endDate = end) => {
      setLoading(true);

      try {
        const data = getUserData();

        // ✅ Add date range to the request body
        const res = await backend.pending({
          ...data,
          start: startDate,
          end: endDate,
        });

        if (res?.error === "False") {
          setPending(res.pending || []);

          setAPending(res.apending || 0);
          setExecuted(res.executed || 0);
          setRejected(res.rejected || 0);
        } else {
          if (res?.logout === 1) {
            window.LogOut?.();
          } else {
            toast.error(res?.message || "Failed to load trade data.");
          }
        }
      } catch (err) {
        console.error("Trade Load Error:", err);
        toast.error("Something went wrong while loading trade data.");
      } finally {
        setLoading(false);
      }
    },
    [getUserData, start, end]
  );

  // 🧩 Sorting function
  const handleSort = useCallback(
    (type) => {
      setSortBy(type);

      let sorted = [...pending];
      switch (type) {
        case "buy_sell":
          sorted.sort((a, b) => b.buy_sell - a.buy_sell);
          break;
        case "sell_buy":
          sorted.sort((a, b) => a.buy_sell - b.profit_loss);
          break;
        case "symbol_asc":
          sorted.sort((a, b) => a.symbol.localeCompare(b.symbol));
          break;
        case "symbol_desc":
          sorted.sort((a, b) => b.symbol.localeCompare(a.symbol));
          break;
        case "time_asc":
          sorted.sort((a, b) => a.date_created - b.date_created);
          break;
        case "time_desc":
        default:
          sorted.sort((a, b) => b.date_created - a.date_created);
          break;
      }

      setPending(sorted);
    },
    [pending]
  );

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const contentHeight = windowSize[1] - 220;

  return (
    <div className="bg-[var(--background)] overflow-hidden">
      {/* Header with sorting */}
      <TopHeader
        sortBy={sortBy}
        onSort={handleSort}
        reload={(start, end) => loadPending(start, end)}
      />

      <HeaderAccount
        apending={apending}
        executed={executed}
        rejected={rejected}
      />

      <div className="border-t border-[var(--hover)]" />

      <div className="px-5 py-3">
        <MiniHeader reload={loadPending} />
        <div
          key={loading ? "loading" : "loaded"}
          style={{
            height: contentHeight,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center w-full h-full">
              <Loading height={contentHeight} />
            </div>
          ) : pending.length > 0 ? (
            <div
              className="custom-scrollbar"
              style={{
                height: contentHeight,
                overflowY: "scroll",
                scrollBehavior: "smooth",
                paddingBottom: 200,
              }}
            >
              {pending.map((item, index) => (
                <PendingCard
                  key={item.id || index}
                  item={item}
                  index={index + 1}
                  reload={loadPending}
                />
              ))}
            </div>
          ) : (
            <NotFound
              small
              height={contentHeight}
              title="Nothing here"
              subtext="No pending found."
            />
          )}
        </div>
      </div>
    </div>
  );
}
